import { defineStore } from 'pinia'
import { buildCatalogMeta, toPocketValue } from '@/utils/propertyCatalog'
import { normalizePropertyCollection } from '@/utils/propertyPayload'
import {
  fetchPropertiesFromApi,
  fetchPropertyDetailsFromApi,
  fetchFilterOptions,
  fetchPropertyAvailability,
  formatApiError
} from '@/services/propertiesApi'

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 15

const DEFAULT_AVAILABILITY_OPTIONS = {
  bookingTypes: ['MONTHLY'],
  defaultPlan: 'MONTHLY',
  currency: 'INR',
  types: [
    { id: 'BOYS', name: 'BOYS' },
    { id: 'GIRLS', name: 'GIRLS' }
  ],
  categories: [{ id: 'PG', name: 'PG' }],
  cities: [],
  areas: [],
  sharingTypes: [],
  roomTypePlans: []
}

const normalizeText = (value) => String(value ?? '').trim()

const toLookupKey = (value) => normalizeText(value).toLowerCase()
const toIdKey = (value) => normalizeText(value)

const toPositiveInteger = (value, fallback) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  return Math.max(1, Math.trunc(parsed))
}

const buildStaticPagination = (items, page = DEFAULT_PAGE, perPage = DEFAULT_PER_PAGE) => {
  const total = Array.isArray(items) ? items.length : 0
  const safePerPage = toPositiveInteger(perPage, DEFAULT_PER_PAGE)
  const safePage = toPositiveInteger(page, DEFAULT_PAGE)
  const lastPage = Math.max(1, Math.ceil(total / safePerPage))
  const currentPage = Math.min(safePage, lastPage)
  const from = total === 0 ? 0 : (currentPage - 1) * safePerPage + 1
  const to = total === 0 ? 0 : Math.min(from + safePerPage - 1, total)

  return {
    total,
    per_page: safePerPage,
    current_page: currentPage,
    last_page: lastPage,
    from,
    to
  }
}

const mapGenderToTypes = (genderValue) => {
  const normalized = toLookupKey(genderValue)
  if (normalized === 'male') {
    return ['BOYS']
  }
  if (normalized === 'female') {
    return ['GIRLS']
  }
  return ['BOYS', 'GIRLS', 'UNISEX']
}

const normalizeOptionList = (items = []) => {
  const list = Array.isArray(items)
    ? items
    : items && typeof items === 'object'
      ? Object.entries(items).map(([key, value]) => {
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            return { key, ...value }
          }
          return { key, value }
        })
      : []
  const seen = new Set()
  const pickFirstText = (candidates = []) =>
    candidates.map((value) => normalizeText(value)).find((value) => value.length > 0) || ''
  const pickHumanText = (candidates = []) => {
    const normalized = candidates.map((value) => normalizeText(value)).filter(Boolean)
    const withLetters = normalized.find((value) => /[a-z]/i.test(value))
    return withLetters || normalized[0] || ''
  }
  const findHumanReadableValue = (value, depth = 0) => {
    if (depth > 3 || value === null || value === undefined) {
      return ''
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const text = normalizeText(value)
      return /[a-z]/i.test(text) ? text : ''
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const discovered = findHumanReadableValue(item, depth + 1)
        if (discovered) {
          return discovered
        }
      }
      return ''
    }

    if (typeof value === 'object') {
      for (const item of Object.values(value)) {
        const discovered = findHumanReadableValue(item, depth + 1)
        if (discovered) {
          return discovered
        }
      }
    }

    return ''
  }

  return list
    .map((item) => {
      if (typeof item === 'string') {
        const value = normalizeText(item)
        if (!value) {
          return null
        }
        return { id: value, name: value }
      }

      if (!item || typeof item !== 'object') {
        return null
      }

      let id = pickFirstText([
        item.value,
        item.Value,
        item.id,
        item.Id,
        item.code,
        item.Code,
        item.key,
        item.Key,
        item.name,
        item.Name,
        item.label,
        item.Label
      ])
      let name = pickHumanText([
        item.key,
        item.Key,
        item.displayLabel,
        item.display_label,
        item.displayName,
        item.display_name,
        item.label,
        item.Label,
        item.name,
        item.Name,
        item.text,
        item.Text,
        item.title,
        item.Title,
        item.id,
        item.Id,
        item.value,
        item.Value,
        item.code,
        item.Code
      ])
      const label = pickHumanText([
        item.key,
        item.Key,
        item.displayLabel,
        item.display_label,
        item.displayName,
        item.display_name,
        item.label,
        item.Label,
        item.name,
        item.Name,
        item.text,
        item.Text,
        item.title,
        item.Title
      ])

      if (!/[a-z]/i.test(name)) {
        const discoveredName = findHumanReadableValue(item)
        if (discoveredName) {
          name = discoveredName
        }
      }

      if (!id && !name) {
        return null
      }

      return {
        id: id || name,
        name: name || id,
        label: label || name || id
      }
    })
    .filter(Boolean)
    .filter((item) => {
      const key = `${item.id}::${item.name}`.toLowerCase()
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
}

const buildFallbackAvailabilityOptions = (properties = []) => {
  const list = Array.isArray(properties) ? properties : []

  const typeSet = new Set()
  const categorySet = new Set()
  const citySet = new Set()
  const areaSet = new Set()

  let currency = DEFAULT_AVAILABILITY_OPTIONS.currency

  list.forEach((property) => {
    mapGenderToTypes(property?.gender).forEach((typeValue) => {
      if (typeValue !== 'UNISEX') {
        typeSet.add(typeValue)
      }
    })

    const category = normalizeText(property?.propertyType).toUpperCase()
    if (category) {
      categorySet.add(category)
    }

    const city = normalizeText(property?.location?.city)
    if (city) {
      citySet.add(city)
    }

    const area = normalizeText(property?.location?.area)
    if (area) {
      areaSet.add(area)
    }

    const propertyCurrency = normalizeText(property?.pricing?.currency)
    if (propertyCurrency) {
      currency = propertyCurrency === '₹' ? 'INR' : propertyCurrency
    }
  })

  const types = [...typeSet]
  const categories = [...categorySet]
  const cities = [...citySet]
  const areas = [...areaSet]

  return {
    bookingTypes: [...DEFAULT_AVAILABILITY_OPTIONS.bookingTypes],
    defaultPlan: DEFAULT_AVAILABILITY_OPTIONS.defaultPlan,
    currency: currency || DEFAULT_AVAILABILITY_OPTIONS.currency,
    types: normalizeOptionList(
      types.length > 0 ? types.map((value) => ({ id: value, name: value })) : DEFAULT_AVAILABILITY_OPTIONS.types
    ),
    categories: normalizeOptionList(
      categories.length > 0
        ? categories.map((value) => ({ id: value, name: value }))
        : DEFAULT_AVAILABILITY_OPTIONS.categories
    ),
    cities: normalizeOptionList(cities.map((value) => ({ id: value, name: value }))),
    areas: normalizeOptionList(areas.map((value) => ({ id: value, name: value }))),
    sharingTypes: [],
    roomTypePlans: []
  }
}

const buildRequestKey = (page, perPage, filters = {}) => {
  const normalizeList = (value) =>
    [...new Set((Array.isArray(value) ? value : []).map((item) => normalizeText(item)).filter(Boolean))].sort()

  const payload = {
    page: toPositiveInteger(page, DEFAULT_PAGE),
    perPage: Math.min(100, toPositiveInteger(perPage, DEFAULT_PER_PAGE)),
    search: normalizeText(filters.search),
    bookingType: normalizeText(filters.bookingType).toUpperCase(),
    types: normalizeList(filters.types),
    categories: normalizeList(filters.categories),
    cities: normalizeList(filters.cities),
    areas: normalizeList(filters.areas),
    min: Number.isFinite(Number(filters.min)) ? Number(filters.min) : null,
    max: Number.isFinite(Number(filters.max)) ? Number(filters.max) : null,
    sort: normalizeText(filters.sort)
  }

  return JSON.stringify(payload)
}

// In-flight promise refs — kept outside Pinia reactive state so
// await works reliably (reactive proxies can interfere with Promises).
let _inflightHome = null
let _inflightList = null
let _inflightOptions = null
let _inflightAll = null
const _inflightDetailsById = new Map()

export const usePropertiesStore = defineStore('properties', {
  state: () => ({
    homeProperties: [],
    listProperties: [],
    catalogProperties: [],
    availabilityOptions: buildFallbackAvailabilityOptions([]),
    pagination: buildStaticPagination([], DEFAULT_PAGE, DEFAULT_PER_PAGE),
    loading: false,
    loadingHome: false,
    loadingList: false,
    loadingOptions: false,
    loadingDetailsById: {},
    error: null,
    optionsError: null,
    detailsErrorById: {},
    detailsFallbackWarningById: {},
    detailsCacheById: {},
    dataSource: 'api',
    homeInitialized: false,
    listInitialized: false,
    optionsInitialized: false,
    initialized: false,
    lastListRequestKey: ''
  }),

  getters: {
    allProperties: (state) => state.catalogProperties,

    featuredProperties: (state) => state.catalogProperties.filter((property) => property.featured),

    getPropertiesByPocket: (state) => (pocketValue) =>
      state.catalogProperties.filter((property) => toPocketValue(property?.location?.pocket) === pocketValue),

    getPropertiesByGender: (state) => (gender) => {
      const normalizedGender = String(gender ?? '').trim().toLowerCase()
      return state.catalogProperties.filter(
        (property) => String(property?.gender ?? '').trim().toLowerCase() === normalizedGender
      )
    },

    getPropertyBySlug: (state) => (slug) =>
      state.catalogProperties.find((property) => property.slug === slug),

    getPropertyById: (state) => (propertyId) => {
      const lookupId = toIdKey(propertyId)
      if (!lookupId) {
        return null
      }

      return state.catalogProperties.find((property) => toIdKey(property?.id) === lookupId) || null
    },

    discountedProperties: (state) => state.catalogProperties.filter((property) => property.discount > 0),

    hotSaleProperties: (state) => state.catalogProperties.filter((property) => property.hotSale),

    getCatalogMeta: (state) => buildCatalogMeta(state.catalogProperties)
  },

  actions: {
    mergeCatalog(properties = []) {
      if (!Array.isArray(properties) || properties.length === 0) {
        return
      }

      const bySlug = new Map(this.catalogProperties.map((property) => [property.slug, property]))
      properties.forEach((property) => {
        if (property?.slug) {
          bySlug.set(property.slug, property)
        }
      })

      this.catalogProperties = [...bySlug.values()]
    },

    getDetailsFallbackWarning(propertyId) {
      const lookupId = toIdKey(propertyId)
      if (!lookupId) {
        return ''
      }

      return this.detailsFallbackWarningById[lookupId] || ''
    },

    async fetchPropertyDetails({
      propertyId = '',
      slug = '',
      force = false
    } = {}) {
      const requestedId = toIdKey(propertyId)
      const fallbackBySlug = slug ? this.getPropertyBySlug(slug) : null
      const resolvedId = requestedId || toIdKey(fallbackBySlug?.id)

      if (!resolvedId) {
        return fallbackBySlug || null
      }

      if (!force && this.detailsCacheById[resolvedId]) {
        return this.detailsCacheById[resolvedId]
      }

      if (this.loadingDetailsById[resolvedId]) {
        return (
          _inflightDetailsById.get(resolvedId) ||
          this.detailsCacheById[resolvedId] ||
          this.getPropertyById(resolvedId) ||
          fallbackBySlug ||
          null
        )
      }

      this.loadingDetailsById = {
        ...this.loadingDetailsById,
        [resolvedId]: true
      }
      this.detailsErrorById = {
        ...this.detailsErrorById,
        [resolvedId]: ''
      }
      this.detailsFallbackWarningById = {
        ...this.detailsFallbackWarningById,
        [resolvedId]: ''
      }

      const promise = (async () => {
        try {
          const payload = await fetchPropertyDetailsFromApi({
            propertyId: resolvedId
          })

          const [normalized] = normalizePropertyCollection(payload ? [payload] : [])
          if (!normalized) {
            throw new Error('API returned no valid property details')
          }

          const mergedProperty = {
            ...(this.getPropertyById(resolvedId) || {}),
            ...normalized
          }

          this.detailsCacheById = {
            ...this.detailsCacheById,
            [resolvedId]: mergedProperty
          }

          this.mergeCatalog([mergedProperty])
          this.dataSource = 'api'

          return mergedProperty
        } catch (error) {
          this.detailsErrorById = {
            ...this.detailsErrorById,
            [resolvedId]: formatApiError(error)
          }

          const fallbackProperty = this.getPropertyById(resolvedId) || fallbackBySlug || null
          if (fallbackProperty) {
            this.detailsFallbackWarningById = {
              ...this.detailsFallbackWarningById,
              [resolvedId]: 'Live property details are unavailable right now. Showing latest cached data.'
            }
            return fallbackProperty
          }

          return null
        } finally {
          this.loadingDetailsById = {
            ...this.loadingDetailsById,
            [resolvedId]: false
          }
          _inflightDetailsById.delete(resolvedId)
        }
      })()

      _inflightDetailsById.set(resolvedId, promise)
      return promise
    },

    async fetchAvailabilityOptions({ force = false } = {}) {
      if (this.loadingOptions) {
        if (_inflightOptions) return _inflightOptions
        this.loadingOptions = false
      }

      if (!force && this.optionsInitialized) {
        return this.availabilityOptions
      }

      this.loadingOptions = true
      this.optionsError = null

      const promise = (async () => {
      try {
        const options = await fetchFilterOptions()
        const fallback = buildFallbackAvailabilityOptions(this.catalogProperties)

        this.availabilityOptions = {
          bookingTypes: Array.isArray(options?.bookingTypes) && options.bookingTypes.length > 0
            ? options.bookingTypes
            : fallback.bookingTypes,
          defaultPlan: normalizeText(options?.defaultPlan) || fallback.defaultPlan,
          currency: normalizeText(options?.currency) || fallback.currency,
          types: Array.isArray(options?.types) && options.types.length > 0 ? options.types : fallback.types,
          categories: Array.isArray(options?.categories) && options.categories.length > 0
            ? options.categories
            : fallback.categories,
          cities: Array.isArray(options?.cities) && options.cities.length > 0 ? options.cities : fallback.cities,
          areas: Array.isArray(options?.areas) && options.areas.length > 0 ? options.areas : fallback.areas,
          sharingTypes: Array.isArray(options?.sharingTypes) && options.sharingTypes.length > 0 ? options.sharingTypes : fallback.sharingTypes,
          roomTypePlans: Array.isArray(options?.roomTypePlans) && options.roomTypePlans.length > 0 ? options.roomTypePlans : fallback.roomTypePlans
        }
        this.dataSource = 'api'
      } catch (error) {
        this.optionsError = formatApiError(error)
        this.availabilityOptions = buildFallbackAvailabilityOptions(this.catalogProperties)
      } finally {
        this.loadingOptions = false
        this.optionsInitialized = true
        _inflightOptions = null
      }

      return this.availabilityOptions
      })()

      _inflightOptions = promise
      return promise
    },

    async fetchHomeProperties({ force = false } = {}) {
      if (this.loadingHome) {
        // Wait for the in-flight request to finish, then return its result
        if (_inflightHome) return _inflightHome
        // Self-heal: flag stuck without a promise
        this.loadingHome = false
      }

      if (!force && this.homeInitialized && this.homeProperties.length > 0) {
        return this.homeProperties
      }

      this.loadingHome = true
      this.error = null

      const promise = (async () => {
      try {
        const { items } = await fetchPropertiesFromApi({
          page: DEFAULT_PAGE,
          perPage: DEFAULT_PER_PAGE
        })

        const normalized = normalizePropertyCollection(items)
        if (normalized.length === 0) {
          throw new Error('API returned no valid properties for home')
        }

        this.homeProperties = normalized
        this.mergeCatalog(normalized)
        this.dataSource = 'api'
      } catch (error) {
        this.error = formatApiError(error)
        // Preserve cached data on refresh failures
        if (!this.homeInitialized) {
          this.homeProperties = []
        }
      } finally {
        this.loadingHome = false
        this.homeInitialized = true
        _inflightHome = null
      }

      return this.homeProperties
      })()

      _inflightHome = promise
      return promise
    },

    async fetchListProperties({
      page = DEFAULT_PAGE,
      perPage = DEFAULT_PER_PAGE,
      filters = {},
      force = false
    } = {}) {
      const safePage = toPositiveInteger(page, DEFAULT_PAGE)
      const safePerPage = Math.min(100, toPositiveInteger(perPage, DEFAULT_PER_PAGE))
      const requestKey = buildRequestKey(safePage, safePerPage, filters)

      if (this.loadingList) {
        // Wait for the in-flight request to finish, then return its result
        if (_inflightList) return _inflightList
        // Self-heal: flag stuck without a promise
        this.loadingList = false
      }

      if (!force && this.listInitialized && this.lastListRequestKey === requestKey) {
        return {
          items: this.listProperties,
          metadata: this.pagination
        }
      }

      this.loadingList = true
      this.error = null

      const promise = (async () => {
      try {
        const { items, metadata } = await fetchPropertiesFromApi({
          page: safePage,
          perPage: safePerPage,
          filters
        })

        const normalized = normalizePropertyCollection(items)
        if (normalized.length === 0 && Number(metadata?.total) > 0) {
          throw new Error('API returned no valid properties for list')
        }

        this.listProperties = normalized
        this.pagination = {
          ...buildStaticPagination(normalized, safePage, safePerPage),
          ...metadata
        }
        this.mergeCatalog(normalized)
        this.lastListRequestKey = requestKey
        this.dataSource = 'api'
      } catch (error) {
        this.error = formatApiError(error)

        this.listProperties = []
        this.pagination = buildStaticPagination([], safePage, safePerPage)
        this.lastListRequestKey = requestKey
      } finally {
        this.loadingList = false
        this.listInitialized = true
        this.initialized = true
        _inflightList = null
      }

      return {
        items: this.listProperties,
        metadata: this.pagination
      }
      })()

      _inflightList = promise
      return promise
    },

    async fetchProperties({ force = false } = {}) {
      if (this.loading) {
        if (_inflightAll) return _inflightAll
        this.loading = false
      }

      this.loading = true

      const promise = (async () => {
      try {
        await Promise.all([
          this.fetchAvailabilityOptions({ force }),
          this.fetchHomeProperties({ force }),
          this.fetchListProperties({
            page: this.pagination.current_page || DEFAULT_PAGE,
            perPage: this.pagination.per_page || DEFAULT_PER_PAGE,
            force
          })
        ])
      } finally {
        this.loading = false
        this.initialized = true
        _inflightAll = null
      }

      return this.catalogProperties
      })()

      _inflightAll = promise
      return promise
    }
  }
})
